import React from 'react';
import { AlertTriangle, Trash2, X, Activity, Users, Building2, Calendar } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';

interface EngagementDeleteDialogProps {
  engagementId: string;
  engagementName: string;
  onClose: () => void;
}

export const EngagementDeleteDialog: React.FC<EngagementDeleteDialogProps> = ({ engagementId, engagementName, onClose }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: impacts, isLoading: loadingImpacts } = useQuery({
    queryKey: ['engagement-deletion-impact', engagementId],
    queryFn: async () => {
      const [sessions, participation, partners, attendance] = await Promise.all([
        supabase.from('sessions').select('*', { count: 'exact', head: true }).eq('engagement_id', engagementId),
        supabase.from('participation').select('*', { count: 'exact', head: true }).eq('engagement_id', engagementId),
        supabase.from('engagement_organizations').select('*', { count: 'exact', head: true }).eq('engagement_id', engagementId),
        supabase.from('attendance').select('*', { count: 'exact', head: true }).in('session_id', (await supabase.from('sessions').select('id').eq('engagement_id', engagementId)).data?.map(s => s.id) || [])
      ]);

      return {
        sessions: sessions.count || 0,
        participation: participation.count || 0,
        partners: partners.count || 0,
        attendance: attendance.count || 0
      };
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      // Deletion logic will cascade at the DB level based on FK settings, 
      // but we explicitly confirm the scope here.
      const { error } = await supabase.from('engagements').delete().eq('id', engagementId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      onClose();
      navigate('/programs');
    }
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)] overflow-hidden border border-rose-200 animate-in zoom-in-95 duration-200">
        <div className="bg-rose-600 p-8 text-white flex items-center justify-between">
           <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center border border-white/20">
                <AlertTriangle size={28} />
              </div>
              <div>
                 <h3 className="text-xl font-black uppercase tracking-tight">Danger Zone</h3>
                 <p className="text-[10px] font-bold text-rose-100 uppercase tracking-widest">Permanent Destruction Protocol</p>
              </div>
           </div>
           <button onClick={onClose} className="p-2 hover:bg-black/10 rounded-lg transition-colors"><X size={24} /></button>
        </div>

        <div className="p-8 space-y-8">
          <div className="space-y-4">
             <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide leading-relaxed">
               You are about to permanently purge the engagement: <br/>
               <span className="text-slate-900 font-black text-sm">"{engagementName}"</span>
             </p>
             
             <div className="bg-slate-50 rounded-xl border border-slate-100 p-6 space-y-4">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-200 pb-2">The following records will be destroyed:</p>
                
                {loadingImpacts ? (
                  <div className="py-6 flex items-center justify-center gap-3">
                    <Activity className="animate-spin text-rose-500" size={18} />
                    <span className="text-[10px] font-black text-slate-300 uppercase animate-pulse">Calculating Blast Radius...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                     <div className="flex items-center gap-3">
                        <Calendar size={14} className="text-rose-500" />
                        <span className="text-[10px] font-black text-slate-900 uppercase">{impacts?.sessions} Sessions</span>
                     </div>
                     <div className="flex items-center gap-3">
                        <Users size={14} className="text-rose-500" />
                        <span className="text-[10px] font-black text-slate-900 uppercase">{impacts?.participation} Enrollees</span>
                     </div>
                     <div className="flex items-center gap-3">
                        <Activity size={14} className="text-rose-500" />
                        <span className="text-[10px] font-black text-slate-900 uppercase">{impacts?.attendance} Attendance Logs</span>
                     </div>
                     <div className="flex items-center gap-3">
                        <Building2 size={14} className="text-rose-500" />
                        <span className="text-[10px] font-black text-slate-900 uppercase">{impacts?.partners} Partner Links</span>
                     </div>
                  </div>
                )}
             </div>
          </div>

          <div className="space-y-3">
            <Button 
              variant="danger" 
              className="w-full py-5 text-sm shadow-xl shadow-rose-200" 
              onClick={() => deleteMutation.mutate()} 
              isLoading={deleteMutation.isPending}
              disabled={loadingImpacts}
              icon={<Trash2 size={20} />}
            >
              I Understand, Purge All Records
            </Button>
            <Button variant="ghost" onClick={onClose} className="w-full py-4 text-slate-400 hover:text-slate-900">
              Abort Deletion
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};